import React, { useMemo } from 'react';
import AsyncPageContent from '../../../../common/components/page/AsyncPageContent';
import { featureApi } from '../api/featureApi';
import {
  HeaderCell,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from 'common/components/Table';
import { orderBy } from 'lodash';
import { Title } from 'common/styles/page';

interface Feature {
  id: number;
  name: string;
  enabled?: boolean;
  parent_feature_id?: number | null;
  parent_feature_name?: string | null;
}

export const FeatureFlagList = () => {
  const { data, error, isLoading } = featureApi.useApiPortalV1FeaturesListQuery(undefined);
  const [updateFeatureState] = featureApi.useApiPortalV1FeaturesPartialUpdateMutation();

  function handleCheckboxClicked(id: number, enabled: boolean) {
    updateFeatureState({
      id,
      patchedFeature: {
        enabled: !enabled,
      },
    });
  }

  // Group features by parent and sort for hierarchical display
  const groupedFeatures = useMemo(() => {
    if (!data) return [];
    
    const features = data as Feature[];
    const parentFeatures: Feature[] = [];
    const childFeaturesByParent: Record<number, Feature[]> = {};
    
    // Separate parent and child features
    features.forEach(feature => {
      if (feature.parent_feature_id) {
        if (!childFeaturesByParent[feature.parent_feature_id]) {
          childFeaturesByParent[feature.parent_feature_id] = [];
        }
        childFeaturesByParent[feature.parent_feature_id].push(feature);
      } else {
        parentFeatures.push(feature);
      }
    });
    
    // Sort parent features by ID
    const sortedParents = orderBy(parentFeatures, 'id');
    
    // Build flat list with parents and their children (only show children if parent is enabled)
    const result: Array<Feature & { isChild?: boolean }> = [];
    
    sortedParents.forEach(parent => {
      result.push(parent);
      
      // Add child features ONLY if parent is enabled
      const children = childFeaturesByParent[parent.id] || [];
      if (children.length > 0 && parent.enabled) {
        const sortedChildren = orderBy(children, 'id');
        sortedChildren.forEach(child => {
          result.push({ ...child, isChild: true });
        });
      }
    });
    
    return result;
  }, [data]);

  return (
    <AsyncPageContent isLoading={isLoading} error={error} data-testid="admin-features-page">
      <Title data-testid="admin-features-header">Feature Flags</Title>
      <Table data-testid="admin-features-table">
        <TableHeader>
          <TableRow>
            <HeaderCell width={8}>Name</HeaderCell>
            <HeaderCell width={6} justify="center">
              Enabled
            </HeaderCell>
            <HeaderCell width={2} justify="center">
              ID
            </HeaderCell>
          </TableRow>
        </TableHeader>

        <TableBody>
          {groupedFeatures.map(feature => (
            <TableRow
              key={feature.id}
              $selectable
              onClick={() => handleCheckboxClicked(feature.id, feature.enabled!!)}
            >
              <TableCell $firstcol style={{ paddingLeft: feature.isChild ? '2rem' : '0.5rem' }}>
                {feature.isChild && <span style={{ color: '#6b7280' }}>└─ </span>}
                {feature.name}
              </TableCell>
              <TableCell justify="center">
                <input
                  data-testid={`admin-features-toggle-${feature.id}`}
                  type="checkbox"
                  checked={feature.enabled}
                  onClick={e => {
                    e.stopPropagation();
                    handleCheckboxClicked(feature.id, feature.enabled!!);
                  }}
                  readOnly
                />
              </TableCell>
              <TableCell justify="center">{feature.id}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </AsyncPageContent>
  );
};
